import {
  PipeRun,
  SewerPipeRun,
  StormSewerRun,
  Manhole,
  PipeDiameterSummary,
} from "../types/tender.types";

export interface PipeRunSummary {
  id: string;
  fromMH: string;
  toMH: string;
  diameter: string;
  length: number;
  averageDepth: number;
  depthCategory: number;
  pipeType?: "clay" | "plastic" | "concrete";
  backfillType?: "type1" | "6F2";
}

export interface DetailedPipeSummary {
  totalLength: number;
  totalRuns: number;
  diameterBreakdown: PipeDiameterSummary[];
  depthCategoryBreakdown: {
    category: string;
    totalLength: number;
    count: number;
    averageDepth: number;
  }[];
  pipeTypeBreakdown: {
    type: string;
    totalLength: number;
    count: number;
    percentage: number;
  }[];
  backfillTypeBreakdown: {
    type: string;
    totalLength: number;
    count: number;
    percentage: number;
  }[];
  deepestRun: {
    fromMH: string;
    toMH: string;
    depth: number;
  } | null;
  longestRun: {
    fromMH: string;
    toMH: string;
    length: number;
  } | null;
  averageRunLength: number;
  averageDepth: number;
}

export interface ManholesSummary {
  totalManholes: number;
  foulManholes: number;
  stormManholes: number;
  bothTypeManholes: number;
  diameterBreakdown: {
    diameter: string;
    count: number;
    percentage: number;
  }[];
  depthBreakdown: {
    range: string;
    count: number;
    percentage: number;
  }[];
  deepestManhole: {
    ref: string;
    depth: number;
    type: string;
  } | null;
  averageDepth: number;
}

/**
 * Calculate detailed pipe run summaries for foul sewers
 */
export const calculateFoulSewersSummary = (
  pipeRuns: SewerPipeRun[],
  manholes: Manhole[]
): DetailedPipeSummary => {
  if (pipeRuns.length === 0) {
    return {
      totalLength: 0,
      totalRuns: 0,
      diameterBreakdown: [],
      depthCategoryBreakdown: [],
      pipeTypeBreakdown: [],
      backfillTypeBreakdown: [],
      deepestRun: null,
      longestRun: null,
      averageRunLength: 0,
      averageDepth: 0,
    };
  }

  const totalLength = pipeRuns.reduce((sum, run) => sum + run.length, 0);
  const totalRuns = pipeRuns.length;

  // Diameter breakdown
  const diameterMap = new Map<string, { totalLength: number; count: number }>();
  pipeRuns.forEach((run) => {
    const existing = diameterMap.get(run.diameter) || {
      totalLength: 0,
      count: 0,
    };
    existing.totalLength += run.length;
    existing.count += 1;
    diameterMap.set(run.diameter, existing);
  });

  const diameterBreakdown = Array.from(diameterMap.entries())
    .map(([diameter, data]) => ({
      diameter,
      totalLength: data.totalLength,
      count: data.count,
    }))
    .sort((a, b) => parseInt(a.diameter) - parseInt(b.diameter));

  // Depth category breakdown
  const depthCategoryMap = new Map<
    number,
    { totalLength: number; count: number; totalDepth: number }
  >();
  pipeRuns.forEach((run) => {
    const category = Math.round(run.averageDepth / 0.25) * 0.25;
    const existing = depthCategoryMap.get(category) || {
      totalLength: 0,
      count: 0,
      totalDepth: 0,
    };
    existing.totalLength += run.length;
    existing.count += 1;
    existing.totalDepth += run.averageDepth;
    depthCategoryMap.set(category, existing);
  });

  const depthCategoryBreakdown = Array.from(depthCategoryMap.entries())
    .map(([category, data]) => ({
      category: `${category.toFixed(2)}m`,
      totalLength: data.totalLength,
      count: data.count,
      averageDepth: data.totalDepth / data.count,
    }))
    .sort((a, b) => parseFloat(a.category) - parseFloat(b.category));

  // Pipe type breakdown
  const pipeTypeMap = new Map<string, { totalLength: number; count: number }>();
  pipeRuns.forEach((run) => {
    const type = run.pipeType || "Inherited";
    const existing = pipeTypeMap.get(type) || { totalLength: 0, count: 0 };
    existing.totalLength += run.length;
    existing.count += 1;
    pipeTypeMap.set(type, existing);
  });

  const pipeTypeBreakdown = Array.from(pipeTypeMap.entries()).map(
    ([type, data]) => ({
      type:
        type === "Inherited"
          ? "Inherited (Global)"
          : type.charAt(0).toUpperCase() + type.slice(1),
      totalLength: data.totalLength,
      count: data.count,
      percentage: (data.totalLength / totalLength) * 100,
    })
  );

  // Backfill type breakdown
  const backfillTypeMap = new Map<
    string,
    { totalLength: number; count: number }
  >();
  pipeRuns.forEach((run) => {
    const type = run.backfillType || "Inherited";
    const existing = backfillTypeMap.get(type) || { totalLength: 0, count: 0 };
    existing.totalLength += run.length;
    existing.count += 1;
    backfillTypeMap.set(type, existing);
  });

  const backfillTypeBreakdown = Array.from(backfillTypeMap.entries()).map(
    ([type, data]) => ({
      type: type === "Inherited" ? "Inherited (Global)" : type.toUpperCase(),
      totalLength: data.totalLength,
      count: data.count,
      percentage: (data.totalLength / totalLength) * 100,
    })
  );

  // Find deepest and longest runs
  const deepestRun = pipeRuns.reduce((deepest, run) => {
    return run.averageDepth > (deepest?.averageDepth || 0) ? run : deepest;
  }, pipeRuns[0]);

  const longestRun = pipeRuns.reduce((longest, run) => {
    return run.length > (longest?.length || 0) ? run : longest;
  }, pipeRuns[0]);

  return {
    totalLength,
    totalRuns,
    diameterBreakdown,
    depthCategoryBreakdown,
    pipeTypeBreakdown,
    backfillTypeBreakdown,
    deepestRun: deepestRun
      ? {
          fromMH: deepestRun.fromMH,
          toMH: deepestRun.toMH,
          depth: deepestRun.averageDepth,
        }
      : null,
    longestRun: longestRun
      ? {
          fromMH: longestRun.fromMH,
          toMH: longestRun.toMH,
          length: longestRun.length,
        }
      : null,
    averageRunLength: totalLength / totalRuns,
    averageDepth:
      pipeRuns.reduce((sum, run) => sum + run.averageDepth, 0) / totalRuns,
  };
};

/**
 * Calculate detailed pipe run summaries for storm sewers
 */
export const calculateStormSewersSummary = (
  pipeRuns: StormSewerRun[],
  manholes: Manhole[]
): DetailedPipeSummary => {
  if (pipeRuns.length === 0) {
    return {
      totalLength: 0,
      totalRuns: 0,
      diameterBreakdown: [],
      depthCategoryBreakdown: [],
      pipeTypeBreakdown: [],
      backfillTypeBreakdown: [],
      deepestRun: null,
      longestRun: null,
      averageRunLength: 0,
      averageDepth: 0,
    };
  }

  const totalLength = pipeRuns.reduce((sum, run) => sum + run.length, 0);
  const totalRuns = pipeRuns.length;

  // Diameter breakdown
  const diameterMap = new Map<string, { totalLength: number; count: number }>();
  pipeRuns.forEach((run) => {
    const existing = diameterMap.get(run.diameter) || {
      totalLength: 0,
      count: 0,
    };
    existing.totalLength += run.length;
    existing.count += 1;
    diameterMap.set(run.diameter, existing);
  });

  const diameterBreakdown = Array.from(diameterMap.entries())
    .map(([diameter, data]) => ({
      diameter,
      totalLength: data.totalLength,
      count: data.count,
    }))
    .sort((a, b) => parseInt(a.diameter) - parseInt(b.diameter));

  // Depth category breakdown
  const depthCategoryMap = new Map<
    number,
    { totalLength: number; count: number; totalDepth: number }
  >();
  pipeRuns.forEach((run) => {
    const category = Math.round(run.averageDepth / 0.25) * 0.25;
    const existing = depthCategoryMap.get(category) || {
      totalLength: 0,
      count: 0,
      totalDepth: 0,
    };
    existing.totalLength += run.length;
    existing.count += 1;
    existing.totalDepth += run.averageDepth;
    depthCategoryMap.set(category, existing);
  });

  const depthCategoryBreakdown = Array.from(depthCategoryMap.entries())
    .map(([category, data]) => ({
      category: `${category.toFixed(2)}m`,
      totalLength: data.totalLength,
      count: data.count,
      averageDepth: data.totalDepth / data.count,
    }))
    .sort((a, b) => parseFloat(a.category) - parseFloat(b.category));

  // Pipe type breakdown
  const pipeTypeMap = new Map<string, { totalLength: number; count: number }>();
  pipeRuns.forEach((run) => {
    const type = run.pipeType || "Inherited";
    const existing = pipeTypeMap.get(type) || { totalLength: 0, count: 0 };
    existing.totalLength += run.length;
    existing.count += 1;
    pipeTypeMap.set(type, existing);
  });

  const pipeTypeBreakdown = Array.from(pipeTypeMap.entries()).map(
    ([type, data]) => ({
      type:
        type === "Inherited"
          ? "Inherited (Global)"
          : type.charAt(0).toUpperCase() + type.slice(1),
      totalLength: data.totalLength,
      count: data.count,
      percentage: (data.totalLength / totalLength) * 100,
    })
  );

  // Backfill type breakdown
  const backfillTypeMap = new Map<
    string,
    { totalLength: number; count: number }
  >();
  pipeRuns.forEach((run) => {
    const type = run.backfillType || "Inherited";
    const existing = backfillTypeMap.get(type) || { totalLength: 0, count: 0 };
    existing.totalLength += run.length;
    existing.count += 1;
    backfillTypeMap.set(type, existing);
  });

  const backfillTypeBreakdown = Array.from(backfillTypeMap.entries()).map(
    ([type, data]) => ({
      type: type === "Inherited" ? "Inherited (Global)" : type.toUpperCase(),
      totalLength: data.totalLength,
      count: data.count,
      percentage: (data.totalLength / totalLength) * 100,
    })
  );

  // Find deepest and longest runs
  const deepestRun = pipeRuns.reduce((deepest, run) => {
    return run.averageDepth > (deepest?.averageDepth || 0) ? run : deepest;
  }, pipeRuns[0]);

  const longestRun = pipeRuns.reduce((longest, run) => {
    return run.length > (longest?.length || 0) ? run : longest;
  }, pipeRuns[0]);

  return {
    totalLength,
    totalRuns,
    diameterBreakdown,
    depthCategoryBreakdown,
    pipeTypeBreakdown,
    backfillTypeBreakdown,
    deepestRun: deepestRun
      ? {
          fromMH: deepestRun.fromMH,
          toMH: deepestRun.toMH,
          depth: deepestRun.averageDepth,
        }
      : null,
    longestRun: longestRun
      ? {
          fromMH: longestRun.fromMH,
          toMH: longestRun.toMH,
          length: longestRun.length,
        }
      : null,
    averageRunLength: totalLength / totalRuns,
    averageDepth:
      pipeRuns.reduce((sum, run) => sum + run.averageDepth, 0) / totalRuns,
  };
};

/**
 * Calculate manholes summary
 */
export const calculateManholesSummary = (
  manholes: Manhole[]
): ManholesSummary => {
  if (manholes.length === 0) {
    return {
      totalManholes: 0,
      foulManholes: 0,
      stormManholes: 0,
      bothTypeManholes: 0,
      diameterBreakdown: [],
      depthBreakdown: [],
      deepestManhole: null,
      averageDepth: 0,
    };
  }

  const totalManholes = manholes.length;
  const foulManholes = manholes.filter((mh) => mh.type === "foul").length;
  const stormManholes = manholes.filter((mh) => mh.type === "storm").length;
  const bothTypeManholes = manholes.filter((mh) => mh.type === "both").length;

  // Diameter breakdown
  const diameterMap = new Map<string, number>();
  manholes.forEach((mh) => {
    const count = diameterMap.get(mh.diameter) || 0;
    diameterMap.set(mh.diameter, count + 1);
  });

  const diameterBreakdown = Array.from(diameterMap.entries())
    .map(([diameter, count]) => ({
      diameter,
      count,
      percentage: (count / totalManholes) * 100,
    }))
    .sort((a, b) => parseInt(a.diameter) - parseInt(b.diameter));

  // Depth breakdown
  const depthRanges = [
    { range: "0-1.5m", min: 0, max: 1.5 },
    { range: "1.5-3m", min: 1.5, max: 3 },
    { range: "3-5m", min: 3, max: 5 },
    { range: "5m+", min: 5, max: Infinity },
  ];

  const depthBreakdown = depthRanges.map((range) => {
    const count = manholes.filter(
      (mh) => mh.depth >= range.min && mh.depth < range.max
    ).length;
    return {
      range: range.range,
      count,
      percentage: (count / totalManholes) * 100,
    };
  });

  // Find deepest manhole
  const deepestManhole = manholes.reduce((deepest, mh) => {
    return mh.depth > (deepest?.depth || 0) ? mh : deepest;
  }, manholes[0]);

  const averageDepth =
    manholes.reduce((sum, mh) => sum + mh.depth, 0) / totalManholes;

  return {
    totalManholes,
    foulManholes,
    stormManholes,
    bothTypeManholes,
    diameterBreakdown,
    depthBreakdown,
    deepestManhole: deepestManhole
      ? {
          ref: deepestManhole.ref,
          depth: deepestManhole.depth,
          type: deepestManhole.type,
        }
      : null,
    averageDepth,
  };
};
